import { Dialog } from "@mui/material"
import { useFormikContext } from "formik"
import { ChevronLeft, X } from "lucide-mui"
import { DateTime } from "luxon"
import React from "react"

import { useLocale } from "@/contexts/locale"
import i18n from "@/i18n"
import { type ReservationFormValues } from "@/pages/public/venue/types"
import { type OpeningHour } from "@/utils/opening_hours"

import DurationScroller from "./mobile_booking_steps/duration_scroller"
import MobileCalendar from "./mobile_booking_steps/mobile_calendar"
import PartySizeStepper from "./mobile_booking_steps/party_size_stepper"
import SheetSubmitBar from "./mobile_booking_steps/sheet_submit_bar"
import TimeScroller from "./mobile_booking_steps/time_scroller"
import { fitsWithinClosingTime } from "./reservation_closing_guard"
import { getReservationDurationOptions } from "./reservation_duration_options"
import { DEFAULT_SIDEBAR_TABLE_MAX_CAPACITY, getSidebarOpeningTimeSlots } from "./reservation_sidebar_availability"

type MobileBookingBottomSheetsProps = {
  open: boolean
  onClose: () => void
  onReserveClick: () => void
  timezone: string
  country: string
  openingHours?: OpeningHour[]
  minAdvanceBookingTime?: number | null
  minReservationTime?: number | null
  maxReservationTime?: number | null
  tables?: Array<{ id: string; minCapacity?: number | null; maxCapacity?: number | null }>
}

const SECTION_LABEL_STYLES = "text-[12px] leading-normal text-[#0c1930]"

const getInitialDurationSeconds = (startTime: string, endTime: string): number | null => {
  if (!startTime || !endTime) return null

  const start = DateTime.fromISO(startTime, { setZone: true })
  const end = DateTime.fromISO(endTime, { setZone: true })
  if (!start.isValid || !end.isValid) return null

  return Math.round(end.diff(start, "seconds").seconds)
}

const MobileBookingBottomSheetsContent: React.FC<MobileBookingBottomSheetsProps> = ({
  onClose,
  onReserveClick,
  timezone,
  country,
  openingHours,
  minAdvanceBookingTime,
  minReservationTime,
  maxReservationTime,
  tables,
}) => {
  const { values, setFieldValue } = useFormikContext<ReservationFormValues>()
  const { locale } = useLocale()

  // "Party not yet chosen" is local state (mirrors the shared flow's confirmedPeopleAmount pattern):
  // peopleAmount is only written to Formik once the user actually picks a size. When the sheet is
  // reopened after a committed selection (startTime + endTime present), all four choices are restored.
  const hasCommittedSelection = Boolean(values.startTime && values.endTime)
  const [partySize, setPartySize] = React.useState<number | null>(() =>
    hasCommittedSelection ? values.peopleAmount : null,
  )
  const [selectedTime, setSelectedTime] = React.useState<string | null>(() => values.startTime || null)
  const [durationSeconds, setDurationSeconds] = React.useState<number | null>(() =>
    getInitialDurationSeconds(values.startTime, values.endTime),
  )

  const maxPartySize = React.useMemo(() => {
    const capacities = (tables || [])
      .map((table) => table.maxCapacity ?? DEFAULT_SIDEBAR_TABLE_MAX_CAPACITY)
      .filter((capacity) => capacity > 0)

    return capacities.length ? Math.max(...capacities) : DEFAULT_SIDEBAR_TABLE_MAX_CAPACITY
  }, [tables])

  const durationOptions = React.useMemo(
    () => getReservationDurationOptions({ minReservationTime, maxReservationTime, locale }),
    [minReservationTime, maxReservationTime, locale],
  )

  const timeSlots = React.useMemo(
    () => getSidebarOpeningTimeSlots(values.date, openingHours, timezone, minAdvanceBookingTime),
    [values.date, openingHours, timezone, minAdvanceBookingTime],
  )

  const handleDateChange = (date: DateTime) => {
    void setFieldValue("date", date)
    // The chosen slot is an absolute timestamp tied to the previous date, so it no longer applies.
    setSelectedTime(null)
  }

  // Party stays local until the CTA (consistent with time/duration); the CTA's handleSubmit is the
  // single commit point. Writing to Formik on every stepper tap would leak into the page-level form
  // on abandon paths (closing the sheet after touching the stepper).
  const handlePartyChange = (next: number | null) => {
    setPartySize(next)
  }

  const canSubmit =
    Boolean(values.date?.isValid) &&
    partySize !== null &&
    Boolean(selectedTime) &&
    durationSeconds !== null &&
    fitsWithinClosingTime({ date: values.date, startTime: selectedTime, durationSeconds, openingHours, timezone })

  const handleSubmit = async () => {
    if (!canSubmit || !values.date || partySize === null || !selectedTime || durationSeconds === null) return

    const endTime = DateTime.fromISO(selectedTime, { setZone: true }).plus({ seconds: durationSeconds }).toISO()

    await setFieldValue("peopleAmount", partySize)
    await setFieldValue("startTime", selectedTime)
    await setFieldValue("endTime", endTime || "")

    onReserveClick()
  }

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      data-testid="public-venue-mobile-booking-sheet"
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-end",
        },
      }}
      PaperProps={{
        "data-testid": "public-venue-mobile-booking-sheet-paper",
        sx: {
          width: "100%",
          margin: 0,
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          maxHeight: "92dvh",
          overflow: "hidden",
        },
      }}
    >
      <div className="flex max-h-[inherit] min-h-0 flex-col">
        <div className="flex h-[50px] shrink-0 items-center justify-between border-b border-[#e5e6ec] px-5">
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center text-[#171717]"
            aria-label={i18n.t("app.public.venue.modal.back")}
            data-testid="public-venue-mobile-booking-sheet-back"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <h2
            className="text-[16px] font-normal leading-4 text-[#19191b]"
            data-testid="public-venue-mobile-booking-sheet-title"
          >
            {i18n.t("app.public.venue.mobile_booking.title")}
          </h2>

          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center text-[#171717]"
            aria-label={i18n.t("app.public.venue.modal.close")}
            data-testid="public-venue-mobile-booking-sheet-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto" data-testid="public-venue-mobile-booking-sheet-body">
          <div className="px-5 py-5">
            <MobileCalendar
              value={values.date}
              timezone={timezone}
              country={country}
              openingHours={openingHours}
              onChange={handleDateChange}
            />
          </div>

          <div className="h-px bg-[#e9e9e9]" />

          <div className="space-y-3 px-5 py-5">
            <p className={SECTION_LABEL_STYLES}>{i18n.t("app.public.venue.mobile_booking.party_size")}</p>
            <PartySizeStepper value={partySize} max={maxPartySize} onChange={handlePartyChange} />
          </div>

          <div className="h-px bg-[#e9e9e9]" />

          <div className="space-y-3 px-5 py-5">
            <p className={SECTION_LABEL_STYLES}>{i18n.t("app.public.venue.mobile_booking.time")}</p>
            <TimeScroller slots={timeSlots} selectedStartTime={selectedTime} onSelect={setSelectedTime} />
          </div>

          <div className="h-px bg-[#e9e9e9]" />

          <div className="space-y-3 px-5 py-5">
            <p className={SECTION_LABEL_STYLES}>{i18n.t("app.public.venue.mobile_booking.duration")}</p>
            <DurationScroller
              options={durationOptions}
              selectedSeconds={durationSeconds}
              onSelect={setDurationSeconds}
            />
          </div>
        </div>

        <SheetSubmitBar disabled={!canSubmit} onSubmit={() => void handleSubmit()} />
      </div>
    </Dialog>
  )
}

const MobileBookingBottomSheets: React.FC<MobileBookingBottomSheetsProps> = (props) => {
  if (!props.open) return null

  return <MobileBookingBottomSheetsContent {...props} />
}

export default MobileBookingBottomSheets
